import { FC } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import LaunchIcon from '@material-ui/icons/Launch';
import GetAppIcon from '@material-ui/icons/GetApp';

const widthLaptop = 370;
const heightLaptop = 268;
const widthTablet = 375 - 2 * 40;
const contentsMarginSide = 12;
const contentsOverlap = 60;
const contentsHeight = 120;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: 'transparent',
      border: 'initial',
      boxShadow: 'initial',
      boxSizing: 'border-box',
      maxWidth: `${widthLaptop}px`,
      minWidth: `${widthTablet}px`,
      width: '100%',
      height: `${heightLaptop}px`,
      overflow: 'visible',
    },
    actionArea: {
      position: 'relative',
      '& .MuiCardActionArea-focusHighlight': {
        display: 'none',
      },
      '& .MuiCardMedia-root': {
        transition: 'all ease-in-out .3s',
      },
      '& .MuiCardContent-root': {
        transition: 'all ease-in-out .3s',
      },
      '& .MuiBox-root': {
        transition: 'all ease-in-out .3s',
      },
      '& .MuiBox-root svg': {
        transition: 'all ease-in-out .3s',
      },
      '&:hover': {
        '& .MuiCardMedia-root': { opacity: 0.7 },
        '& .MuiBox-root svg': { color: '#333333' },
        '& .MuiCardContent-root': { boxShadow: '0px 8px 16px #00000040' },
      },
      '&:active': {
        '& .MuiCardMedia-root': { opacity: 0.7 },
        '& .MuiBox-root': { backgroundColor: '#C2CAD8' },
        '& .MuiBox-root svg': { color: '#333333' },
        '& .MuiCardContent-root': { boxShadow: '0px 8px 16px #00000040' },
      },
      '&:focus': {
        '& .MuiCardMedia-root': { opacity: 0.7 },
        '& .MuiBox-root': { backgroundColor: '#EDEFF3' },
        '& .MuiBox-root svg': { color: '#333333' },
        '& .MuiCardContent-root': { boxShadow: '0px 8px 16px #00000040' },
      },
    },
    mediaBack: {
      margin: 0,
      padding: 0,
      background: 'transparent',
      backgroundColor: '#333333',
    },
    media: {
      backgroundColor: '#c2cad8',
      border: '1px solid #0000001a',
      boxSizing: 'border-box',
      width: '100%',
      height: `${heightLaptop - contentsHeight + contentsOverlap}px`,
    },
    content: {
      backgroundColor: '#ffffff',
      boxShadow: '0px 8px 16px #00000014',
      boxSizing: 'border-box',
      borderRadius: '8px',
      padding: theme.spacing(11 / 8, 2, 1.5, 2),
      position: 'relative',
      height: `${contentsHeight}px`,
      margin: `-${contentsOverlap}px ${contentsMarginSide}px 0`,
    },
    contentHeading: {
      display: 'flex',
      height: '32px',
      marginBottom: '5px',
      '& span': {
        flexGrow: 1,
        fontSize: '17px',
        fontWeight: 'bold',
        lineHeight: '25px',
        margin: '5px auto 2px',
      },
    },
    iconArea: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      '& > svg': {
        color: '#aaaaaa',
        fontSize: '14px',
        margin: '9px',
      },
    },
    contentDescription: {
      fontSize: '13px',
      lineHeight: '18px',
    },
  }),
);

export interface LinkCardProps {
  iconVariant?: 'none' | 'newtab' | 'download';
  imgSrc?: string;
  imgAlt?: string;
  headingText?: React.ReactNode;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
}

export const LinkCard: FC<LinkCardProps> = ({
  iconVariant = 'none',
  imgSrc,
  imgAlt = '',
  headingText = '',
  onClick,
  children,
  className = '',
}: LinkCardProps) => {
  const classes = useStyles();

  const iconVariantToIcon = new Map<string, JSX.Element>([
    ['none', <></>],
    [
      'newtab',
      <Box className={classes.iconArea}>
        <LaunchIcon />
      </Box>,
    ],
    [
      'download',
      <Box className={classes.iconArea}>
        <GetAppIcon viewBox="1 1 23 23" />
      </Box>,
    ],
  ]);

  return (
    <Card className={`${classes.root} ${className}`}>
      <CardActionArea
        onClick={onClick}
        disableRipple
        className={classes.actionArea}
      >
        <Container
          disableGutters
          maxWidth={false}
          className={classes.mediaBack}
        >
          {imgSrc ? (
            <CardMedia
              className={classes.media}
              component="img"
              src={imgSrc}
              alt={imgAlt}
            />
          ) : (
            <CardMedia className={classes.media} component="div" />
          )}
        </Container>
        <CardContent className={classes.content}>
          <Typography component="h3" className={classes.contentHeading}>
            <span>{headingText}</span>
            {iconVariantToIcon.get(iconVariant) ||
              iconVariantToIcon.get('none')}
          </Typography>
          <Typography component="p" className={classes.contentDescription}>
            {children}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
